import ReactDOM from "react-dom";
import { merge, isObject, set, isEmpty } from "lodash";
import {
  getGridRecordOpenWithoutFilter,
  deleteGridRecord,
  statusUpdateGridRecord,
} from "./actions";
import { toAPIDate } from "../formatter";
import moment from "moment";

class OpenWithoutFilterGrid {
  /**
   * Get instance for utitlity
   *
   * @param object
   * @param callback
   * @return object
   */
  static getInstance(argsObj) {
    return new OpenWithoutFilterGrid(argsObj);
  }

  /**
   * Class initializer
   *
   * @param object
   * @param callback
   * @return void
   */
  constructor(argsObj) {
    this.currentComponent = argsObj.currentComponent;
    this.initialState = this.currentComponent.initialState();
    this.url = argsObj.url;
    this.action = argsObj.action || getGridRecordOpenWithoutFilter;
    this.actionType = argsObj.actionType;
    this.extraParams = argsObj.extraParams;
    this.currentPage = parseInt(argsObj.currentPage) || 1;
    this.rowsPerPage = parseInt(argsObj.rowsPerPage) || 10;

    this.rowsPerPageOptions = {
      5: 5,
      10: 10,
      25: 25,
      50: 50,
    };

    this.selectedRowPerPage = 0;
  }

  /**rowsPerPage
   * build the query
   *
   * @return object
   */
  getQueryParams() {
    const extraParams = this.extraParams || {};
    let filters =
      this.currentComponent.state &&
      typeof this.currentComponent.state.filters === "object"
        ? { ...this.currentComponent.state.filters }
        : {};

    const searchVal =
      isObject(filters.search) ? filters.search.value : filters.search;
    if (searchVal && typeof searchVal === "string" && searchVal.trim() !== "") {
      delete filters.fromDate;
      delete filters.toDate;
    }

    const dateKeysToParse = ["fromDate", "toDate"];
    Object.keys(filters).forEach((k) => {
      if (dateKeysToParse.includes(k)) filters[k] = toAPIDate(filters[k]);
    });

    // remove empty keys and organize wildcard search
    Object.keys(filters).forEach((k) => {
      if (isObject(filters[k])) {
        if (!filters[k]["value"]) {
          delete filters[k];
        } else if (filters[k]["wildcard"]) {
          filters[k] = { like: `%${filters[k]["value"]}%`, options: "i" };
        }
      } else !filters[k] && delete filters[k];
    });

    let params = merge(
      {
        // add key only if data available
        ...(this.sortByFieldName && {
          order: `${this.sortByFieldName} ${this.sortType}`,
        }),
        skip: this.currentPage * this.rowsPerPage - this.rowsPerPage,
        limit: this.rowsPerPage,
      },
      extraParams,
      { ...filters } // Include filters directly without the 'where' key
    );

    return params;
  }

  /**
   * Join the nested objects by using dots
   *
   * @param object filters
   * @param string filterKey
   */
  formatFilters(filters, filterKey = "") {
    const self = this;
    let validFilters = {};
    Object.keys(filters).forEach((filter) => {
      const filterName = !isEmpty(filterKey)
        ? `${filterKey}.${filter}`
        : filter;
      if (isObject(filters[filter])) {
        const subValidFilters = self.formatFilters(filters[filter], filterName);
        validFilters = merge(validFilters, subValidFilters);
        return;
      }

      const filterVal = filters[filter].trim();
      if (!isEmpty(filterVal)) {
        validFilters[filterName] = filterVal;
      }
    });
    return validFilters;
  }

  /**
   * Build the head column filter to pass in query string
   *
   * @return object
   */
  getFilters(filters) {
    const validFilters = this.formatFilters(filters);
    return !isEmpty(validFilters)
      ? JSON.stringify(validFilters).replace("{", "").replace("}", "")
      : "";
  }

  /**
   * Update the header filter state value
   */
  updateColFiltersState = (name, value, getRows = false) => {
    let filters = { ...this.currentComponent.state.filters };
    // Check wether object for wildcard search
    filters = isObject(filters[name])
      ? set(filters, name, { ...filters[name], value })
      : set(filters, name, value);

    if (name === "search") {
      const hasSearch =
        value != null && typeof value === "string" && value.trim() !== "";
      if (hasSearch) {
        if ("fromDate" in filters) filters.fromDate = "";
        if ("toDate" in filters) filters.toDate = "";
      } else {
        if ("fromDate" in filters && !filters.fromDate) {
          filters.fromDate = moment().format("DD-MM-YYYY");
        }
      }
    }

    this.currentComponent.setState({ filters }, () => {
      getRows && this.currentComponent.grid.getRecords();
    });
  };

  /**
   * Handle grid column filter text change
   */
  handleColFiltersChange = (e) => {
    const getRows = e.target.type === "select-one";
    // remove when input has prefix as "filter"
    const strings = e.target.name.split("_");
    this.updateColFiltersState(
      strings[1] || strings[0],
      e.target.value,
      getRows
    );
  };

  /**
   * Handle grid column filter keyDown
   */
  handleColFiltersKeyDown = (e) => {
    // Call table header filter when user press 'Enter key'
    const code = e.keyCode || e.which || e.keycode;
    if (code === 13) {
      if (typeof e.preventDefault === "function") {
        e.preventDefault();
      }
      this.handleFilter();
    }
  };

  handleCommonFilterChange = (e) => {
    this.currentComponent.setState({ commonFilter: e.target.value });
  };

  /**
   * Handle grid column filter select input change
   */
  handleColFiltersSelectChange(name, value) {
    this.updateColFiltersState(name, value);
  }

  /**
   * Handle grid column filter change
   */
  handleColSorting = (columnName) => () => {
    if (this.sortByFieldName !== columnName) {
      this.sortByFieldName = columnName;
      this.sortType = "ASC";
    } else {
      this.sortType = this.sortType === "DESC" ? "ASC" : "DESC"; // (-1 - DESC, 1 - ASC)
    }

    this.getRecords();
  };

  /**
   * Handle grid global search change
   */
  handleGlobalFiltersChange = (e) => {
    // This Comment code required in future
    this.currentComponent.setState({ search: e.target.value });
  };

  /**
   * Handle grid column filter change
   */
  handleDateFiltersChange = (name, date) => {
    /** Declare the handle Select Change */
    const dateString = date.format("DD-MM-YYYY");
    const filters = Object.assign({}, this.currentComponent.state.filters);
    filters[name] = dateString;
    this.currentComponent.setState({ filters: filters });
  };
  /**
   * Declare the handle Select Change
   */
  handleSelectChange(selected, value) {
    const selectedDom = ReactDOM.findDOMNode(selected);
    const name = selectedDom.getAttribute("data-name")
      ? selectedDom.getAttribute("data-name")
      : selectedDom.getAttribute("name");
    const filters = Object.assign({}, this.currentComponent.state.filters);
    filters[name] = value;
    this.currentComponent.setState({ filters: filters, search: value });
  }

  /**
   * Handle the pagination click
   */
  handlePaginateClick = (page, pageRows) => {
    this.currentPage = page;
    if (this.rowsPerPage !== pageRows) {
      this.resetPagination();
    }
    this.rowsPerPage = parseInt(pageRows);
    this.getRecords();
  };

  /**
   * Handle the No Of Entries change
   */
  handleNumberEntryChange = (page, pageRows) => {
    this.handlePaginateClick(page, pageRows);
  };

  /**
   * Set additional parameters into queryparams
   */
  setExtraParams(extraParams) {
    this.extraParams =
      extraParams && typeof extraParams === "object"
        ? merge(this.extraParams, extraParams)
        : null;
  }
  /**
   * Method to remove extra param
   */
  removeExtraParam() {
    this.extraParams = null;
  }

  /**
   * Get the records for table
   */
  getRecords(extraParams) {
    if (extraParams) {
      this.setExtraParams(extraParams);
    }
    return this.currentComponent.props.dispatch(
      this.action({
        url: this.url,
        actionType: this.actionType,
        queryParams: this.getQueryParams(),
      })
    );
  }

  /**
   * Handle the delete funtionality
   */
  deleteRecord(deleteModalObj) {
    if (typeof deleteModalObj !== "object") {
      // This is the error statement to the developers
      console.error('The "Delete Record" method arguments must be an object.');
      return;
    }

    const action = deleteModalObj.action || deleteGridRecord;
    return this.currentComponent.props.dispatch(action(deleteModalObj));
  }

  /**
   * Handle the status update functionality
   */
  statusUpdate(statusUpdateObj, e) {
    e.preventDefault();
    if (typeof statusUpdateObj !== "object") {
      // This is the error statement to the developers
      console.error('The "Status Update" method arguments must be an object.');
      return;
    }

    const action = statusUpdateObj.action || statusUpdateGridRecord;
    return this.currentComponent.props.dispatch(action(statusUpdateObj));
  }

  /**
   * Handle the status update functionality
   */
  statusUpdateUser(statusUpdateObj, e) {
    e.preventDefault();
    if (typeof statusUpdateObj !== "object") {
      // This is the error statement to the developers
      console.error('The "Status Update" method arguments must be an object.');
      return;
    }

    const action = statusUpdateObj.action || statusUpdateGridRecord;
    return this.currentComponent.props.dispatch(action(statusUpdateObj));
  }

  /**
   * Handle the column based filter
   */
  handleFilter() {
    this.resetPagination();
    this.getRecords();
  }

  /**
   * Handle the column based filter reset
   */
  handleResetFilter() {
    this.resetPagination();
    this.currentComponent.setState(
      { filters: this.initialState.filters, search: this.initialState.search },
      () => this.handleFilter()
    );
  }

  /**
   * Reset the pagination
   */
  resetPagination = () => {
    this.currentPage = 1;
    this.rowsPerPage = 10;
  };

  /**
   * When user bulk delete the all the of the current page,
   * then currentPage is more than 0, need to reset the page to one level back
   * So it will fetch the previous page record.
   * Suppose if we do not reset to one page back, then it will show the 'No record exist' message on current page
   *
   */
  getRecordsAfterDelete() {
    // Make nessasray changes and get record after delete
    this.getRecords();
  }
}

export default OpenWithoutFilterGrid;
